import React, { useState } from 'react';
import { Button } from './ui/button';
import { 
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription 
} from './ui/dialog';
import { Share2, Check, Copy, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';

/**
 * SendToFriend Component
 * 
 * Reusable component for sharing vehicle listings.
 * Can be used on public vehicle pages for visitor-initiated sharing.
 * 
 * Reuses the same backend share-links endpoint as host Share Listing.
 */

// Share button icons as inline SVGs (same as VehicleManagement.js)
const WhatsAppIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
  </svg>
);

const EmailIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="2" y="4" width="20" height="16" rx="2"/>
    <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/>
  </svg>
);

const SMSIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
  </svg>
);

/**
 * Copy link to clipboard helper
 */
const copyToClipboard = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
};

/**
 * Check if native Web Share API is available
 */
const canUseNativeShare = () => {
  return typeof navigator !== 'undefined' && 
         typeof navigator.share === 'function' &&
         typeof navigator.canShare === 'function';
};

/**
 * SendToFriend Modal Content
 * Displays share options in a clean, conversion-focused layout
 */
export const SendToFriendModal = ({ 
  isOpen, 
  onClose, 
  shareLinks,
  vehicleName,
  vehicleYear,
  dailyRate
}) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    const success = await copyToClipboard(shareLinks.listing_url);
    if (success) {
      setCopied(true);
      toast.success('Link copied!');
      setTimeout(() => setCopied(false), 2000);
    } else {
      toast.error('Failed to copy');
    }
  };

  const handleNativeShare = async () => {
    if (!canUseNativeShare() || !shareLinks.share_options.native_share) return;
    
    try {
      const shareData = shareLinks.share_options.native_share;
      if (navigator.canShare(shareData)) {
        await navigator.share(shareData);
      }
    } catch (err) {
      // User cancelled or share failed - silently ignore
      if (err.name !== 'AbortError') {
      }
    }
  };

  if (!shareLinks) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-sm sm:max-w-md mx-4">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-slate-800">
            <Share2 className="w-5 h-5 text-[#004B8D]" />
            Send to a Friend
          </DialogTitle>
          <DialogDescription>
            Know someone who needs this vehicle? Share it with them!
          </DialogDescription>
        </DialogHeader>
        
        <div className="py-4 space-y-4">
          {/* Vehicle Summary */}
          <div className="bg-slate-50 border border-slate-100 rounded-lg p-3">
            <p className="font-medium text-slate-900 text-sm">
              {vehicleYear ? `${vehicleYear} ` : ''}{vehicleName}
            </p>
            <p className="text-sm text-slate-500">${dailyRate}/day</p>
          </div>
          
          {/* Copy Link */}
          <div className="flex gap-2">
            <input 
              type="text" 
              value={shareLinks.listing_url} 
              readOnly 
              className="flex-1 px-3 py-2 bg-slate-100 border border-slate-200 rounded-lg text-sm truncate"
              data-testid="send-friend-url"
            />
            <Button 
              variant="outline"
              onClick={handleCopy}
              className="min-w-[70px]"
              data-testid="send-friend-copy"
            >
              {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
              <span className="ml-1">{copied ? 'Copied' : 'Copy'}</span>
            </Button>
          </div>
          
          {/* Native Share Button (if supported) */}
          {canUseNativeShare() && (
            <Button 
              onClick={handleNativeShare}
              className="w-full bg-[#004B8D] hover:bg-[#003a6d] h-11"
              data-testid="send-friend-native"
            >
              <Share2 className="w-4 h-4 mr-2" />
              Share via Device
            </Button>
          )}
          
          {/* Share Options Grid */}
          <div className="grid grid-cols-2 gap-2">
            <a 
              href={shareLinks.share_options.whatsapp} 
              target="_blank" 
              rel="noopener noreferrer"
              className="block"
            >
              <Button 
                variant="outline" 
                className="w-full justify-start gap-2 h-10 text-green-600 border-green-200 hover:bg-green-50"
                data-testid="send-friend-whatsapp"
              >
                <WhatsAppIcon />
                WhatsApp
              </Button>
            </a>
            
            <a 
              href={shareLinks.share_options.sms}
              className="block"
            >
              <Button 
                variant="outline" 
                className="w-full justify-start gap-2 h-10 text-teal-600 border-teal-200 hover:bg-teal-50"
                data-testid="send-friend-sms"
              >
                <SMSIcon />
                SMS
              </Button>
            </a>
            
            <a 
              href={shareLinks.share_options.email}
              className="block col-span-2"
            >
              <Button 
                variant="outline" 
                className="w-full justify-start gap-2 h-10 text-slate-600 border-slate-200 hover:bg-slate-50"
                data-testid="send-friend-email"
              >
                <EmailIcon />
                Email
              </Button>
            </a>
          </div>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/**
 * SendToFriend Section Component
 * Displays a clean CTA section on the vehicle page
 */
export const SendToFriendSection = ({ 
  vehicleId, 
  vehicleName, 
  vehicleYear, 
  dailyRate,
  api 
}) => {
  const [showModal, setShowModal] = useState(false);
  const [shareLinks, setShareLinks] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleOpenShare = async () => {
    if (shareLinks) {
      setShowModal(true);
      return;
    }

    setLoading(true);
    try {
      // Reuse the same share-links endpoint as host Share Listing
      const response = await api.get(`/vehicles/${vehicleId}/share-links`);
      setShareLinks(response.data);
      setShowModal(true);
    } catch (error) {
      toast.error('Failed to load share options');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Send to Friend CTA Section */}
      <div 
        className="bg-gradient-to-r from-blue-50 to-slate-50 rounded-2xl border border-blue-100 p-5"
        data-testid="send-to-friend-section"
      >
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#004B8D]/10 flex items-center justify-center flex-shrink-0">
            <MessageCircle className="w-5 h-5 text-[#004B8D]" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-medium text-slate-900 mb-1">
              Know someone who needs this vehicle?
            </p>
            <p className="text-sm text-slate-500 mb-3">
              Send this listing to a friend, family member, or colleague
            </p>
            <Button
              variant="outline"
              size="sm"
              onClick={handleOpenShare}
              disabled={loading}
              className="h-9 border-[#004B8D]/30 text-[#004B8D] hover:bg-[#004B8D]/5"
              data-testid="send-to-friend-btn"
            >
              {loading ? (
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-slate-300 border-t-[#004B8D] mr-2" />
              ) : (
                <Share2 className="w-4 h-4 mr-2" />
              )}
              Send to a Friend
            </Button>
          </div>
        </div>
      </div>

      {/* Share Modal */}
      <SendToFriendModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        shareLinks={shareLinks}
        vehicleName={vehicleName}
        vehicleYear={vehicleYear}
        dailyRate={dailyRate}
      />
    </>
  );
};

export default SendToFriendSection;
